// Lifted from useSequentialTableInfo; pure so basic-output can reuse without React/Jotai.
//
// Design: "fat input, dumb function". The wrapping hook resolves Jotai context
// and sibling hooks and passes the already-resolved values here. Basic-output
// (offline render) passes trivial defaults for the UI-driven inputs (sort,
// highlight, group-name editing) and reuses the same transform logic, so the
// rendered output cannot drift from the existing CustomizedAnalysisPage.

import { type TFunction } from 'i18next'
import { produce } from 'immer'
import * as R from 'remeda'

import {
  calcHeatmapRatio,
  type CellInfo,
  compact,
  type DataDisplayType,
  getDiffKey,
  getDiffText,
  type Group,
  groupAllLabelI18nKey,
  type GroupAndAll,
  type IconName,
  NumFormat,
  type RowInfo,
} from '~/utils'

import {
  type GroupsDiff,
  type GroupsDiffColumnKey,
  type RowData,
} from './common'
import { type SequentialColumnKey } from './sequential'

type SequentialTableInfoStyles = {
  answerText: string
  questionText: string
}

type RowGetters<T extends RowData<GroupAndAll | GroupsDiffColumnKey>> = {
  getAnswerText: (rowData: T) => string
  getQuestionText: (rowData: T) => string
  getRowId: (rowData: T) => string
}

export type SequentialBuildInput<
  TAnswer extends RowData<GroupAndAll | GroupsDiffColumnKey>,
  TCustomerAttribute extends RowData<GroupAndAll | GroupsDiffColumnKey>,
> = {
  addHighlightProps: (key: string) => Partial<CellInfo>
  customerAttributesRowGetters: RowGetters<TCustomerAttribute>
  dataDisplayType: DataDisplayType
  diffMinMaxMap: Record<GroupsDiffColumnKey, { max: number; min: number }>
  getSortIcon: (
    columnKey: GroupAndAll | GroupsDiffColumnKey,
  ) => Extract<IconName, 'sort-asc' | 'sort-desc'> | 'blank'
  groupAndAllNameMap: Record<GroupAndAll, string>
  groupsDiffs: ReadonlyArray<GroupsDiff>
  mapGroup: <V>(
    mapFunc: (group: GroupAndAll, index: number) => V,
  ) => Record<GroupAndAll, V>
  mapGroupsDiff: <V>(
    mapFunc: (groupsDiff: GroupsDiff, index: number) => V,
  ) => Record<GroupsDiffColumnKey, V>
  normalAnswersRowGetters: RowGetters<TAnswer>
  setNameEditingGroup: (group: Group) => void
  // Sort callback applied per-question; interleaves with row building (mirrors
  // the hook's existing getDataRowInfos). Pass identity for offline render.
  sortFunc: <T extends RowData<GroupAndAll | GroupsDiffColumnKey>>(
    rowsData: T[],
  ) => T[]
  styles: SequentialTableInfoStyles
  t: TFunction
  tamRatio: number
  totalData: {
    counts: Record<GroupAndAll, number>
    ratios: Record<GroupAndAll, number>
  }
  visibleAnswersArrayData: TAnswer[][]
  visibleCustomerAttributesArrayData: TCustomerAttribute[][]
}

export const buildSequentialTableInfo = <
  TAnswer extends RowData<GroupAndAll | GroupsDiffColumnKey>,
  TCustomerAttribute extends RowData<GroupAndAll | GroupsDiffColumnKey>,
>({
  addHighlightProps,
  customerAttributesRowGetters,
  dataDisplayType,
  diffMinMaxMap,
  getSortIcon,
  groupAndAllNameMap,
  groupsDiffs,
  mapGroup,
  mapGroupsDiff,
  normalAnswersRowGetters,
  setNameEditingGroup,
  sortFunc,
  styles,
  t,
  tamRatio,
  totalData,
  visibleAnswersArrayData,
  visibleCustomerAttributesArrayData,
}: SequentialBuildInput<
  TAnswer,
  TCustomerAttribute
>): RowInfo<SequentialColumnKey>[] => {
  const titleRowInfo: RowInfo<SequentialColumnKey> = {
    questionText: { isColumnHeader: true },
    answerText: {
      align: 'right',
      isColumnHeader: true,
      isHeader: true,
      text: t('group_1'),
    },
    ...mapGroup((group) =>
      produce<CellInfo>(
        {
          align: 'center',
          icon: getSortIcon(group),
          isBorderShownOnHoverWhenClickable: true,
          isColumnHeader: true,
          isHeader: true,
        },
        (draft) => {
          if (group === 'all') {
            draft.text = t(groupAllLabelI18nKey)
          } else {
            draft.group = group
            draft.text = groupAndAllNameMap[group]
            draft.onClick = () => {
              setNameEditingGroup(group)
            }
          }
        },
      ),
    ),
    ...mapGroupsDiff((groupsDiff): CellInfo => ({
      align: 'center',
      icon: getSortIcon(getDiffKey(groupsDiff)),
      isColumnHeader: true,
      isHeader: true,
      text: getDiffText(groupsDiff, {
        textFn: (item) => groupAndAllNameMap[item],
      }),
    })),
  }

  const getDataRowInfos = <
    T extends RowData<GroupAndAll | GroupsDiffColumnKey>,
  >(
    rowsArrayData: T[][],
    { getAnswerText, getQuestionText, getRowId }: RowGetters<T>,
  ): RowInfo<SequentialColumnKey>[] =>
    rowsArrayData.flatMap(
      // 選択肢 / 顧客属性
      R.piped(
        sortFunc,
        (rowsData) =>
          rowsData.map<RowInfo<SequentialColumnKey>>(
            (rowData, rowDataIndex) => {
              const questionText: CellInfo | undefined =
                rowDataIndex === 0
                  ? {
                      align: 'left',
                      className: styles.questionText,
                      isHeader: true,
                      text: getQuestionText(rowData),
                      rowSpan: rowsData.length,
                    }
                  : undefined

              return {
                questionText,
                answerText: {
                  align: 'right',
                  className: styles.answerText,
                  isHeader: true,
                  text: getAnswerText(rowData),
                  textCharacterLimit: 30,
                },
                ...mapGroup((group) => {
                  const ratio = rowData.ratios[group]
                  const count = rowData.counts[group]

                  const key = `${getRowId(rowData)}-${group}`

                  return {
                    align: 'center',
                    ratio,
                    text: NumFormat.forCount(
                      count,
                      ratio,
                      tamRatio,
                      dataDisplayType,
                    ),
                    ...addHighlightProps(key),
                  }
                }),
                ...mapGroupsDiff((groupsDiff): CellInfo => {
                  const key = getDiffKey(groupsDiff)
                  const ratio = rowData.ratios[key]
                  const count = rowData.counts[key]
                  const [text, copyValue] = NumFormat.countToPts(
                    count,
                    ratio,
                    tamRatio,
                    dataDisplayType,
                  )
                  const { max, min } = diffMinMaxMap[key]

                  const diffKey = `diff-${getRowId(rowData)}-${key}`

                  return {
                    align: 'center',
                    copyValue,
                    heatmap: true,
                    ratio: calcHeatmapRatio(ratio, max, min),
                    text,
                    ...addHighlightProps(diffKey),
                  }
                }),
              }
            },
          ),
        // 設問 / 顧客属性グループ間のタイトル
        R.concat([titleRowInfo]),
      ),
    )

  const answersRowInfos = getDataRowInfos(
    visibleAnswersArrayData,
    normalAnswersRowGetters,
  )
  const customerAttributesRowInfos = getDataRowInfos(
    visibleCustomerAttributesArrayData,
    customerAttributesRowGetters,
  )

  return compact([
    // ヘッダー
    produce(titleRowInfo, (draft) => {
      const cellInfos = compact([
        draft.questionText,
        ...groupsDiffs.map((groupsDiff) => draft[getDiffKey(groupsDiff)]),
      ])
      for (const cellInfo of cellInfos) {
        cellInfo.rowSpan = 2
      }
    }),
    // TOTAL
    {
      answerText: {
        align: 'right',
        isColumnHeader: true,
        isHeader: true,
        text: 'TOTAL',
      },
      ...mapGroup((group): CellInfo => {
        const count = totalData.counts[group]
        const ratio = totalData.ratios[group]

        return {
          align: 'center',
          isColumnHeader: true,
          text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
        }
      }),
    },
    ...answersRowInfos,
    ...customerAttributesRowInfos,
  ])
}
